import {useState, useEffect, useRef, useCallback} from 'react';
import { API_BASE, WS_BASE } from '../shared';

// Plain WebSocket, matching the backend's actual /live-queue protocol
// (app/modules/websocket/router.py): a JSON {"action","topic"} envelope and
// a `?token=` query param, NOT STOMP framing -- this used to connect via
// SockJS+stomp.js, which sends real STOMP CONNECT/SUBSCRIBE frames the
// backend's `websocket.receive_json()` can't parse, so every connection
// silently failed and fell back to this hook's own 5s reconnect loop
// forever. One topic, agent/{id}/incoming-call, carries typed messages:
// INCOMING_CALL (ring) and CALL_CLOSED (another agent took it, or it was missed).
export function useAgentSocket(agentId, onMessage) {
  const wsRef           = useRef(null);
  const reconnectRef    = useRef(null);
  const mountedRef      = useRef(true);
  const onMessageRef    = useRef(onMessage);
  const agentIdRef      = useRef(agentId);
  const [wsStatus, setWsStatus] = useState('disconnected');

  useEffect(() => { onMessageRef.current = onMessage; });
  useEffect(() => { agentIdRef.current = agentId;           });

  const connect = useCallback(() => {
    const aid = agentIdRef.current;
    if (!aid) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setWsStatus('connecting');
    try {
      const token = localStorage.getItem('agent_token') || '';
      const url = new URL('/live-queue', WS_BASE || API_BASE || window.location.origin);
      if (!WS_BASE) url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      if (token) url.searchParams.set('token', token);

      const ws = new WebSocket(url.toString());

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        wsRef.current = ws;
        setWsStatus('connected');
        ws.send(JSON.stringify({ action: 'subscribe', topic: `agent/${aid}/incoming-call` }));
      };

      ws.onmessage = (event) => {
        let data;
        try { data = JSON.parse(event.data); } catch { return; }
        if (data && typeof data === 'object' && data.type !== 'error') onMessageRef.current?.(data);
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        console.warn('WS disconnected, reconnecting in 5s…');
        setWsStatus('disconnected');
        wsRef.current = null;
        reconnectRef.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => ws.close();
    } catch (err) {
      console.warn('WS connect error:', err);
      setWsStatus('disconnected');
      reconnectRef.current = setTimeout(connect, 5000);
    }
  }, []);

  useEffect(() => {
    if (!agentId) return;
    mountedRef.current = true;
    connect();

    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [agentId, connect]);

  return { wsStatus };
}
